import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DEMO_TOTAL_MS = 60_000;
const DEMO_SEATS = ['compass', 'iron', 'sage'] as const;
const DEMO_SEAT_MS = DEMO_TOTAL_MS / DEMO_SEATS.length; // 20s per seat

export default function Landing() {
  const { t } = useTranslation();
  const demoRef = useRef<HTMLDivElement | null>(null);
  const [demoPlaying, setDemoPlaying] = useState(false);
  const [activeSeatIdx, setActiveSeatIdx] = useState(-1);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!demoPlaying) return;
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setElapsedMs(elapsed);
      const idx = Math.floor(elapsed / DEMO_SEAT_MS);
      if (idx >= DEMO_SEATS.length) {
        setDemoPlaying(false);
        setActiveSeatIdx(-1);
        setElapsedMs(0);
        window.clearInterval(tick);
        return;
      }
      setActiveSeatIdx(idx);
    }, 200);
    return () => window.clearInterval(tick);
  }, [demoPlaying]);

  const startDemo = () => {
    demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setDemoPlaying(true);
    setActiveSeatIdx(0);
    setElapsedMs(0);
  };

  return (
    <>
      {/* Hero */}
      <section className="max-w-page mx-auto px-8 lg:px-16 xl:px-24 pt-24 pb-16 text-center">
        <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-meta uppercase text-vermilion-700 mb-6 px-4 py-1 border border-vermilion-100 bg-vermilion-50 rounded-full">
          ◆ {t('hero.eyebrow')}
        </span>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight mb-6 max-w-4xl mx-auto">
          {t('hero.title')}
          <br />
          <em className="not-italic text-vermilion font-medium">{t('hero.titleEm')}</em>
        </h1>
        <p className="text-lg text-ink-2 max-w-xl mx-auto mb-10 leading-relaxed">{t('hero.sub')}</p>
        <div className="flex justify-center gap-3 flex-wrap mb-6">
          <Link to="/auth" className="btn btn-primary btn-large">
            {t('hero.ctaPrimary')}
          </Link>
          <button
            type="button"
            className="btn btn-secondary btn-large"
            onClick={startDemo}
            data-testid="hero-cta-demo"
            aria-controls="demo-card"
          >
            {demoPlaying ? t('hero.ctaSecondaryPlaying') : t('hero.ctaSecondary')}
          </button>
        </div>
        <p className="font-mono text-[11px] tracking-wider uppercase text-ink-3">
          <strong className="text-ink-2 font-medium">{t('hero.trustFree')}</strong> · {t('hero.trustNoCard')} · {t('hero.trustBuilder')}
        </p>
      </section>

      {/* Demo card */}
      <div
        id="demo-card"
        ref={demoRef}
        className="max-w-3xl mx-auto px-8 lg:px-16 xl:px-24 mt-16 scroll-mt-24"
        data-testid="demo-card"
        data-demo-playing={demoPlaying}
      >
        <div
          className={
            'bg-paper border rounded-lg p-12 shadow-[0_4px_16px_rgba(26,24,20,0.08)] relative transition-all duration-300 ' +
            (demoPlaying ? 'border-vermilion shadow-[0_4px_24px_rgba(193,40,38,0.18)]' : 'border-hairline')
          }
        >
          {/* Live progress strip — visible only while demo plays */}
          {demoPlaying && (
            <div className="absolute -top-px left-0 right-0 h-px bg-hairline overflow-hidden rounded-t-lg" data-testid="demo-progress">
              <div
                className="h-full bg-vermilion transition-[width] ease-linear"
                style={{ width: `${Math.min(100, (elapsedMs / DEMO_TOTAL_MS) * 100)}%`, transitionDuration: '200ms' }}
              />
            </div>
          )}

          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-hairline">
            <span
              className={
                'inline-flex items-center gap-1.5 font-mono text-[11px] tracking-meta uppercase ' +
                (demoPlaying ? 'text-vermilion-700' : 'text-azure-700')
              }
            >
              <span className={'w-2 h-2 rounded-full animate-pulse ' + (demoPlaying ? 'bg-vermilion' : 'bg-azure')} />
              {demoPlaying
                ? t('demo.liveNow', { sec: Math.max(0, Math.ceil((DEMO_TOTAL_MS - elapsedMs) / 1000)) })
                : t('demo.live')}
            </span>
            <span className="ml-auto font-mono text-[11px] text-ink-3">{t('demo.round')}</span>
          </div>

          {DEMO_SEATS.map((seat, idx) => {
            const role = t(`demo.${seat}.role`);
            const text = t(`demo.${seat}.text`);
            const isCritic = seat === 'iron';
            const isActive = demoPlaying && activeSeatIdx === idx;
            const isPast = demoPlaying && activeSeatIdx > idx;
            const isFuture = demoPlaying && activeSeatIdx < idx;
            return (
              <div
                key={seat}
                className={
                  'grid grid-cols-[100px_1fr] gap-4 py-3 items-baseline rounded-md px-3 -mx-3 transition-all duration-300 ' +
                  (isActive ? 'bg-vermilion-50/40 ring-1 ring-vermilion-100 ' : '') +
                  (isPast ? 'opacity-60 ' : '') +
                  (isFuture ? 'opacity-40 ' : '')
                }
                data-testid={`demo-seat-${seat}`}
                data-demo-state={isActive ? 'active' : isPast ? 'past' : isFuture ? 'future' : 'idle'}
              >
                <span className="font-mono text-[11px] tracking-wider uppercase text-ink-3">
                  <strong className="text-ink font-semibold capitalize">{seat}</strong>
                  <br />
                  <span className={isCritic ? 'text-vermilion-700' : ''}>{role}</span>
                </span>
                <p className="text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Value props */}
      <section id="how" className="section">
        <span className="section-eyebrow">{t('value.eyebrow')}</span>
        <h2 className="section-title">{t('value.title')}</h2>
        <p className="section-sub">{t('value.sub')}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(['card1', 'card2', 'card3'] as const).map((k) => {
            const num = t(`value.${k}.num`);
            const title = t(`value.${k}.title`);
            const title2 = t(`value.${k}.title2`, { defaultValue: '' });
            const body = t(`value.${k}.body`);
            return (
              <div key={k} className="card card-hover">
                <div className="font-serif text-6xl font-extralight text-vermilion leading-none mb-4">{num}</div>
                <h3 className="font-serif text-2xl font-medium leading-tight mb-3">
                  {title}
                  {title2 && <><br />{title2}</>}
                </h3>
                <p className="text-[14.5px] leading-relaxed text-ink-2">{body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works · 4 steps */}
      <section className="section">
        <span className="section-eyebrow">{t('how.eyebrow')}</span>
        <h2 className="section-title">{t('how.title')}</h2>

        <ol className="mt-8">
          {(['step1', 'step2', 'step3', 'step4'] as const).map((k, idx) => (
            <li
              key={k}
              className="grid grid-cols-[80px_1fr] gap-8 py-8 border-b border-hairline last:border-0 items-baseline"
            >
              <span className="font-mono text-sm font-medium tracking-wider text-vermilion">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div>
                <h4 className="font-serif text-2xl font-medium leading-tight mb-1.5">{t(`how.${k}.title`)}</h4>
                <p className="text-[15px] leading-relaxed text-ink-2 max-w-xl">{t(`how.${k}.body`)}</p>
                <div className="font-mono text-[11px] tracking-wider text-ink-3 mt-1.5">{t(`how.${k}.meta`)}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Pricing teaser */}
      <section className="section text-center">
        <span className="section-eyebrow">{t('pricingTeaser.eyebrow')}</span>
        <h2 className="section-title mx-auto">{t('pricingTeaser.title')}</h2>
        <p className="section-sub mx-auto mb-8">{t('pricingTeaser.sub')}</p>
        <Link to="/pricing" className="btn btn-primary btn-large">
          {t('pricingTeaser.cta')}
        </Link>
      </section>

      {/* CTA strip */}
      <section className="bg-paper border-y border-hairline py-24 px-8 lg:px-16 xl:px-24 text-center">
        <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight mb-3 max-w-2xl mx-auto leading-tight">
          {t('ctaStrip.title')}
        </h2>
        <p className="text-base text-ink-2 mb-8 max-w-xl mx-auto">{t('ctaStrip.sub')}</p>
        <Link to="/auth" className="btn btn-vermilion btn-large">
          {t('ctaStrip.cta')}
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-hairline py-12 px-8 lg:px-16 xl:px-24 font-mono text-[11px] tracking-wider text-ink-3 text-center">
        <div className="flex justify-center gap-6 mb-3 flex-wrap">
          <Link to="/" className="hover:text-ink transition">{t('nav.home')}</Link>
          <Link to="/pricing" className="hover:text-ink transition">{t('nav.pricing')}</Link>
          <a href="#" className="hover:text-ink transition">{t('footer.privacy')}</a>
          <a href="#" className="hover:text-ink transition">{t('footer.terms')}</a>
          <a href="https://github.com/trine777/fyd" target="_blank" rel="noreferrer" className="hover:text-ink transition">
            GitHub
          </a>
        </div>
        <div>FYD · Find Your Direction · {t('footer.tagline')}</div>
      </footer>
    </>
  );
}
